/**
 * Locale state. The 2.5 equivalent of `bimgo-ai-web`'s `LanguageProvider`,
 * trimmed to what the desktop needs: a persisted `en`/`zh` choice and a `t()`
 * lookup over `strings.ts`. No network — the preference is a per-browser
 * convenience, stored in `localStorage`. Without a saved choice, the desktop's
 * primary OS language decides the default; older bridges use navigator.language.
 *
 * `t(key)` returns the string for the active locale, falling back to the key
 * itself so a not-yet-translated string is visible rather than blank. `{name}`
 * placeholders are interpolated from the optional `vars`.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { LOCALES, STRINGS, type Locale } from "./strings";

const STORAGE_KEY = "bimgo_locale";

interface LocaleValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleValue>({
  locale: "en",
  setLocale: () => undefined,
  t: (key) => key,
});

function savedLocale(): Locale | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (LOCALES as readonly string[]).includes(saved)) return saved as Locale;
  } catch {
    // Private mode / blocked storage — use the system language.
  }
  return null;
}

function initialLocale(): Locale {
  const saved = savedLocale();
  if (saved) return saved;
  const nav = typeof navigator !== "undefined" ? navigator.language.toLowerCase() : "";
  return nav.startsWith("zh") ? "zh" : "en";
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const manualChoice = useRef(false);
  const [systemReady, setSystemReady] = useState(
    () => savedLocale() !== null || typeof window === "undefined" || !window.bimgo?.app?.systemLanguages,
  );

  useEffect(() => {
    const detect = window.bimgo?.app?.systemLanguages;
    if (savedLocale() || !detect) return;
    let cancelled = false;
    void detect().then((languages) => {
      if (!cancelled && !manualChoice.current && languages[0]) {
        setLocaleState(languages[0].toLowerCase().startsWith("zh") ? "zh" : "en");
      }
    }).catch(() => {
      // Older/offline bridges keep the navigator-based fallback.
    }).finally(() => {
      if (!cancelled) setSystemReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    // Mirror the choice into the desktop shell so its native strings (the update prompts) match.
    // Fires on mount too, seeding settings.json from the web's current locale. No-op in a browser
    // (no `window.bimgo`) and best-effort — a persistence failure never affects the UI.
    if (systemReady) void window.bimgo?.app?.setLocale?.(locale).catch(() => undefined);
  }, [locale, systemReady]);

  const setLocale = useCallback((next: Locale) => {
    manualChoice.current = true;
    setSystemReady(true);
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A remembered locale is a convenience; never let a storage failure throw.
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      let value = STRINGS[locale][key] ?? STRINGS.en[key] ?? key;
      if (vars) {
        for (const [name, v] of Object.entries(vars)) {
          value = value.replace(new RegExp(`\\{${name}\\}`, "g"), String(v));
        }
      }
      return value;
    },
    [locale],
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleValue {
  return useContext(LocaleContext);
}
